import { SafePromise, safeMap } from '@benzinga/safe-await';
import { Card, CheckoutSubscription, Coupon, Plan, Product } from './entities';

export interface IngressProduct {
  features: IngressFeature[];
  name: string;
  packages: IngressPackage[];
  plans: IngressPlan[];
  premium_features: IngressFeature[];
  slug: string;
  uuid: string;
}

export interface IngressFeature {
  available_packages: string[];
  base_price: string;
  description: string;
  is_hidden: boolean;
  name: string;
  slug: string;
  uuid: string;
}

export interface IngressPackage {
  checkout_url?: string;
  description: string;
  features: string[];
  name: string;
  preview_description: string;
  preview_link: string;
  slug: string;
  uuid: string;
}

export interface IngressPlan {
  interval: string;
  interval_count: number;
  name: string;
  package: string;
  price: string;
  slug: string;
  trial_period_days: number;
  uuid: string;
}

const transformProduct = (ingress: IngressProduct): Product => ({
  features: ingress.features.map(feature => ({
    availablePackages: feature.available_packages,
    basePrice: feature.base_price,
    description: feature.description,
    isHidden: feature.is_hidden,
    name: feature.name,
    slug: feature?.slug,
    uuid: feature.uuid,
  })),
  name: ingress.name,
  packages: ingress.packages.map(p => ({
    checkoutUrl: p.checkout_url,
    description: p.description,
    features: p.features,
    name: p.name,
    previewDescription: p.preview_description,
    previewLink: p.preview_link,
    slug: p.slug,
    uuid: p.uuid,
  })),
  plans: ingress.plans.map<Plan>(
    plan =>
      ({
        interval: plan.interval as 'year' | 'month',
        intervalCount: plan.interval_count as 1 | 3 | 24,
        name: plan.name,
        package: plan.package,
        price: plan.price,
        slug: plan?.slug,
        trialPeriodDays: plan.trial_period_days,
        uuid: plan.uuid,
      }) as unknown as Plan,
  ),
  premiumFeatures: ingress.premium_features.map(feature => ({
    availablePackages: feature.available_packages,
    basePrice: feature.base_price,
    description: feature.description,
    isHidden: feature.is_hidden,
    name: feature.name,
    slug: feature?.slug,
    uuid: feature.uuid,
  })),
  slug: ingress?.slug,
  uuid: ingress.uuid,
});

export const ingressProduct = (ingressProduct: SafePromise<IngressProduct>): SafePromise<Product> =>
  safeMap(ingressProduct, transformProduct);

export const ingressProducts = (ingressProducts: SafePromise<IngressProduct[]>): SafePromise<Product[]> =>
  safeMap(ingressProducts, ingressProducts => ingressProducts.map(transformProduct));

export declare type CouponDuration = 'once' | 'repeating' | 'forever';
export interface IngressCoupon {
  amount_off: number | null;
  code: string | null;
  currency: string | null;
  duration: CouponDuration;
  duration_in_months: number | null;
  is_valid: boolean;
  name: string;
  percent_off: number | null;
  plans: string[] | null;
  redeem_by: string | null;
}

export const ingressCoupon = (ingress: SafePromise<IngressCoupon>): SafePromise<Coupon> =>
  safeMap(ingress, ingress => ({
    amountOff: ingress.amount_off,
    code: ingress.code,
    currency: ingress.currency,
    duration: ingress.duration,
    durationInMonths: ingress.duration_in_months,
    isValid: ingress.is_valid,
    name: ingress.name,
    percentOff: ingress.percent_off,
    plans: ingress.plans,
  }));

export interface IngressLineItem {
  amount: number;
  currency: string;
  date_created: string;
  feature_name: string;
  feature_slug: string;
  feature_uuid: string;
  localized_price: string;
  uuid: string;
}

export declare type CheckoutSubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid';
export declare type CheckoutSubscriptionInterval = 'day' | 'week' | 'month' | 'year';
export interface IngressCheckoutSubscription {
  amount: number;
  base_plan: string;
  cancel_at_period_end: boolean;
  coupon: string | null;
  currency: string;
  date_created: string;
  interval: CheckoutSubscriptionInterval;
  interval_count: number;
  is_paying_subscription: boolean;
  line_items: IngressLineItem[];
  localized_price: string;
  plan_name: string;
  product: string;
  product_name: string;
  status: CheckoutSubscriptionStatus;
  trial_end: string;
  uuid: string;
}

export const ingressCheckoutSubscription = (
  ingress: SafePromise<IngressCheckoutSubscription>,
): SafePromise<CheckoutSubscription> =>
  safeMap(ingress, ingress => ({
    amount: ingress.amount,
    basePlan: ingress.base_plan,
    cancelAtPeriodEnd: ingress.cancel_at_period_end,
    coupon: ingress.coupon,
    currency: ingress.currency,
    dateCreated: ingress.date_created,
    interval: ingress?.interval,
    intervalCount: ingress?.interval_count,
    isPayingSubscription: ingress.is_paying_subscription,
    lineItems: ingress.line_items.map(lineItem => ({
      amount: lineItem.amount,
      currency: lineItem.currency,
      dateCreated: lineItem.date_created,
      featureName: lineItem.feature_name,
      featureSlug: lineItem.feature_slug,
      featureUuid: lineItem.feature_uuid,
      localizedPrice: lineItem.localized_price,
      uuid: lineItem.uuid,
    })),
    localizedPrice: ingress.localized_price,
    planName: ingress.plan_name,
    product: ingress.product,
    productName: ingress.product_name,
    status: ingress.status,
    trialEnd: ingress.trial_end,
    uuid: ingress.uuid,
  }));

export interface IngressCard {
  brand: string;
  country: string;
  exp_month: number;
  exp_year: number;
  last_4: string;
  name: string;
  uuid: string;
  zip_code: string;
}

export const ingressCard = (ingress: SafePromise<IngressCard>): SafePromise<Card> =>
  safeMap(ingress, ingress => ({
    brand: ingress.brand,
    country: ingress.country,
    expMonth: ingress.exp_month,
    expYear: ingress.exp_year,
    last4: ingress.last_4,
    name: ingress.name,
    uuid: ingress.uuid,
    zipCode: ingress.zip_code,
  }));

export const ingressCards = (ingress: SafePromise<IngressCard[]>): SafePromise<Card[]> =>
  safeMap(ingress, ingress =>
    ingress.map(ingress => ({
      brand: ingress.brand,
      country: ingress.country,
      expMonth: ingress.exp_month,
      expYear: ingress.exp_year,
      last4: ingress.last_4,
      name: ingress.name,
      uuid: ingress.uuid,
      zipCode: ingress.zip_code,
    })),
  );
